import Observer from '~/utils/lib/core/observer'
import { ifElse, includes, propEq } from '~/utils/lib/core/prelude'

/**
 * @constant {string}
 * @default
 */
export const STORE_PREFIX = 'prepaPaie'

/** @constant {string} */
export const baseURL = import.meta.env.VITE_PREPA_PAIE_API_URL

/**
 * @typedef {object} RequestConstructorParams
 * @property {string} options.method
 * @property {string} options.route
 * @property {object} [options.data]
 * @property {object} [options.params] will hydrate URL params
 */

/**
 * @typedef {object} RequestResponse
 * @property {*} data
 * @property {number} status
 * @property {string} statusText
 * @property {object} headers
 * @property {RequestConfig} config
 * @property {XMLHttpRequest} request
 * @link https://axios-http.com/docs/res_schema
 */

/**
 * @typedef {Error & {
 *    config: RequestConfig
 *    isAxiosError: boolean
 *    request?: XMLHttpRequest
 *    response?: RequestResponse
 * }} RequestError
 * @desc extending `Error` with instance extra properties
 * @link https://axios-http.com/docs/handling_errors
 */

/**
 * @typedef {import('axios').AxiosRequestConfig & {
 *  withAuthorization: boolean;
 *  execCount: number;
 * }} RequestConfig
 * @desc extending Axios config with extra config properties
 * @link https://axios-http.com/docs/req_config
 */

/** @type {Observer} */
export const observer = new Observer()

/** @constant {object} */
export const ERRORS = {
  REQUEST_RESPONSE_UNKNOWN_SCHEMA: '<RequestError> with unknown schema',
  REQUEST_RESPONSE_UNKNOWN_DATA: '<RequestError.data> with unknown schema',
  REQUEST_RESPONSE_UNKNOWN_MESSAGE: '<RequestError.data> with unknown "message"',
  REQUEST_RESPONSE_UNKNOWN_CAUSE: '<RequestError.data> with unknown "cause"',
}

/**
 * @constant {object}
 * @readonly
 */
export const MICRO_SERVICE_ERRORS = /** @type {const} */ ({
  MISC: 'miscelleanous micro service error returned',
})


/**
 * @typedef {RequestResponseReflection_Right | RequestResponseReflection_Left} RequestResponseReflection
 * @desc
 *    Micro service success request response reflection,
 *    "contextual response" pattern (including a discriminating property
 *    `status` in an "either~like" pattern)
 *
 * @typedef {Object} RequestResponseReflection_Right
 * @property {REQUEST_RESPONSE_STATUS["SUCCESS"]} status
 * @property {RequestResponse["data"]} data
 *
 * @typedef {Object} RequestResponseReflection_Left
 * @property {REQUEST_RESPONSE_STATUS["ERROR"]} status
 * @property {RequestResponse["data"]} error
 */

/**
 * @typedef {Object} REQUEST_RESPONSE_STATUS
 * @property {string} [SUCCESS="success"]
 * @property {string} [ERROR="error"]
 */
export const REQUEST_RESPONSE_STATUS = {
  SUCCESS: 'success',
  ERROR: 'error',
}

/**
 * @desc Reverse reflection of the `RequestResponseReflection` into a Promise
 * @type {(_: RequestResponseReflection) => Promise<
 *  RequestResponseReflection_Right["data"]
 * >} or rejects `RequestResponseReflection_Left["error"]`
 */
// @ts-expect-error: TypeScript currying limitation
export const requestResponseReflectionToPromise = ifElse(
  (/** @type {RequestResponseReflection} */ res) => propEq('status', REQUEST_RESPONSE_STATUS.SUCCESS, res),
  (/** @type {RequestResponseReflection_Right} */ { data }) => Promise.resolve( data ),
  (/** @type {RequestResponseReflection_Left} */ { error }) => Promise.reject( error )
)
/**
 * @desc Predicate discriminating a union
 * @param {RequestResponse["data"]} results
 * @type {(_: any | RequestResponseReflection) => boolean}
 * @returns {boolean}
 */
export const responseResponseReflectionPredicate = results => {
  return includes(
    results.status || '',
    [
      REQUEST_RESPONSE_STATUS.SUCCESS,
      REQUEST_RESPONSE_STATUS.ERROR
    ]
  )
}
